import React, { useState, useEffect, useMemo } from 'react';
import { find, findIndex, head, pathOr, propEq, sortBy } from 'ramda';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import uniq from 'lodash/uniq';
import { Col, Row } from 'react-bootstrap';
import {
  Button,
  Box,
  Link,
  Stack,
  Typography,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import {
  ModalConfirm,
  useConfirmModal,
} from '@fortress-technology-solutions/fortress-component-library/Molecules_Fortress';
import { palette } from '@fortress-technology-solutions/fortress-component-library/design';
import { FormattedMessage } from 'react-intl';

import moment from 'moment';
import {
  ELECTRONIC_SIGNING_METHOD,
  getSentAndExecutedStatus,
} from '../../../utils/lease-helpers';
import ElementWithPermissions from '../../../components/ElementWithPermissions';
import UnitAccessibilityInfo from '../../../components/UnitAccessibilityInfo';
import messages from './messages';
import type { Application } from '../../CreateApplication/types';
import type { FloorPlan } from '../../ManageFloorPlans/types';
import type { ActivityType } from '../../App/types';
import type { Intl } from '../../../types';
import styled from 'styled-components';
import { filterFRApplicants } from '../ProfileDetails/index';
import {
  DetailList,
  Divider,
} from '../../../components/ProfileVisualComponents';
import FormattedDateOrDashes from '../../../components/FormattedDateOrDashes';
import FloorPlanDetailRow from '../../../components/FloorPlanDetailRow';
import AssignUnitLink from '../../../containers/ApplicationProfile/Snapshot/AssignUnitLink';
import DesiredMoveInDateRow from './DesiredMoveInDateRow';
import { getDesiredMoveInDate } from '../../ManageProspects/utils';
import { useFlags } from 'launchdarkly-react-client-sdk';
import IconTitle from '../../../components/ProfileVisualComponents/IconTitle';

type Props = {
  currentRecord: Application,
  currentUser: Object,
  users: Array<Object>,
  intl: Intl,
  activityTypes: Array<ActivityType>,
  handleAssignedToChange: Function,
  handleConvertToResident: Function,
  handleConvertToCommercialTenant: Function,
  hasCompletedQualifications: boolean,
  history: Object,
  assignedUnits: Array<Object>,
  floorPlans: ?(FloorPlan[]),
  unassignUnitCallback: Function,
  scheduledMoveInDate: string,
  applicationId: string,
  getUnitQuote: Function,
  prospectId: string,
  paymentRestrictions: Object,
  rentStartDate: string,
  propertyId: string,
  organizationId?: string,
  householdId?: string,
  disableUnassignUnitLink?: boolean,
  isAffordable: boolean,
  canAssignUnitBeforeFee?: boolean,
  canAssignUnassignUnit?: boolean,
  onSelectFloorPlan?: Function,
  onDesiredMoveInDateSaved?: Function,
};
const LabelProfileDetails = styled.label.attrs({
  className: 'profile-details',
})``;

export const ButtonsRow = ({
  handleConvertToResident,
  handleConvertToCommercialTenant,
  convertButtonDisabled,
}: any) => {
  let ConvertButton = null;
  if (handleConvertToResident) {
    ConvertButton = (
      <Button
        onClick={handleConvertToResident}
        type="button"
        variant="action"
        disabled={convertButtonDisabled}
      >
        <FormattedMessage {...messages.convertToResident} />
      </Button>
    );
  }

  if (handleConvertToCommercialTenant) {
    ConvertButton = (
      <Button
        variant={'action'}
        onClick={handleConvertToCommercialTenant}
        type="button"
        disabled={convertButtonDisabled}
      >
        <FormattedMessage {...messages.convertToCommercialTenant} />
      </Button>
    );
  }

  return (
    <Row>
      <Col xs={12} sm={6}>
        <ElementWithPermissions scope={['resident-create']}>
          {ConvertButton}
        </ElementWithPermissions>
      </Col>
    </Row>
  );
};

export const Snapshot = ({
  currentRecord,
  currentUser,
  users,
  intl,
  activityTypes,
  handleAssignedToChange,
  history,
  assignedUnits,
  floorPlans,
  handleConvertToResident,
  handleConvertToCommercialTenant,
  hasHouseholdRentableItems,
  unassignUnitCallback,
  applicationId,
  scheduledMoveInDate,
  getUnitQuote,
  prospectId,
  onPaymentRestrictionChange,
  paymentRestrictions,
  rentStartDate,
  hasCompletedQualifications,
  refreshActivityTable,
  areApplicationFeesPaid,
  propertyId,
  organizationId,
  householdId,
  disableUnassignUnitLink,
  isAffordable,
  canAssignUnitBeforeFee = false,
  canAssignUnassignUnit = false,
  onSelectFloorPlan,
  onDesiredMoveInDateSaved,
}: Props) => {
  const { rentableItems, affordableWaitlistV2 } = useFlags();

  const { openConfirm, openConfirmModal, closeConfirmModal } =
    useConfirmModal();
  const [confirmModalContentState, setConfirmModalContentState] = useState({
    title: '',
    body: '',
  });

  // Clear confirm modal content when it closes
  useEffect(() => {
    if (!openConfirm) {
      setConfirmModalContentState({ title: '', body: [] });
    }
  }, [openConfirm]);

  const prospectInfo = currentRecord.prospectInfo || {
    lastActivity: null,
    prospectPreferences: { moveInDateFrom: null },
  };
  const prospectPreferences = prospectInfo.prospectPreferences || {
    moveInDateFrom: null,
  };

  const lastActivityType = prospectInfo.lastActivity
    ? find(propEq('id', prospectInfo.lastActivity.activityTypeId))(
        activityTypes,
      )
    : null;
  const lastActivityDateFormatted = prospectInfo.lastActivity
    ? moment(
        prospectInfo.lastActivity.endTime ||
          prospectInfo.lastActivity.createdAt,
      ).format('MMM DD, YYYY')
    : '---';

  const assignees = useMemo(() => {
    const assignee = currentRecord.assignedTo;

    // $FlowFixMe
    const assigneeIndex = findIndex(
      (x) => x.value === currentRecord.assignedToId,
      users,
    );

    const options =
      assigneeIndex < 0 && assignee
        ? sortBy(
            (x) => {
              return x.text.toLowerCase();
            },
            [
              {
                value: currentRecord.assignedToId,
                text: `${assignee.firstName} ${assignee.lastName}`,
              },
            ].concat(users),
          )
        : users;

    if (!currentRecord.assignedTo) {
      options.unshift({
        value: '',
        text: intl.formatMessage(messages.unassigned),
        disabled: true,
      });
    }
    return options;
  }, [currentRecord.assignedTo, currentRecord.assignedToId, users, intl]);
  const assignedUnit =
    assignedUnits && !!head(assignedUnits) ? head(assignedUnits) : null;

  const unitNumber = pathOr(
    intl.formatMessage(messages.notAssigned),
    ['unit', 'number'],
    // $FlowFixMe
    assignedUnit,
  );

  const fpUnitRentRestrictions = uniq(
    (currentRecord?.au?.unit?.floorPlan?.fprr ?? [])
      .map((u) => u.unitRentRestriction)
      .filter((urr) => !isNil(urr))
      .sort(
        (a, b) =>
          parseFloat(a?.replace('%', '')) - parseFloat(b?.replace('%', '')),
      ),
  ).join('/');
  const unitRentRestriction = isEmpty(fpUnitRentRestrictions)
    ? (currentRecord?.au?.unit?.floorPlan?.unitRentRestriction ?? '').trim()
    : fpUnitRentRestrictions;

  const visibleApplicants = filterFRApplicants(currentRecord.applicants);
  const applicantNames = visibleApplicants
    .filter((applicant) =>
      pathOr(false, ['applicantCustomer', 'isProspect'], applicant),
    )
    .map((applicant) => {
      // $FlowFixMe
      const first = pathOr(
        null,
        ['applicantCustomer', 'customer', 'firstName'],
        applicant,
      );
      // $FlowFixMe
      const last = pathOr(
        null,
        ['applicantCustomer', 'customer', 'lastName'],
        applicant,
      );
      const name = [];
      if (first) {
        name.push(first);
      }
      if (last) {
        name.push(last);
      }
      return name.join(' ');
    })
    .join(', ');

  const unassignUnit = () => {
    const { lease } = currentRecord;
    const { leaseExecuted, leaseSentToPortal } =
      getSentAndExecutedStatus(lease);
    const isElectronic =
      lease.desiredSignatureMethod === ELECTRONIC_SIGNING_METHOD;

    let title = `${intl.formatMessage(
      messages.confirmUnassignUnit,
    )} ${applicantNames}?`;

    let body = '';
    if (leaseExecuted && isElectronic) {
      body = intl.formatMessage(messages.cancelElectronic);
    }
    if (leaseExecuted && !isElectronic) {
      body = intl.formatMessage(messages.cancelManual);
    }
    if (leaseSentToPortal) {
      body = intl.formatMessage(messages.confirmEditElectronicLease);
    }
    if (rentableItems && hasHouseholdRentableItems) {
      body = intl.formatMessage(messages.confirmHouseholdRentableItemsRemoval);
    }
    if (hasCompletedQualifications) {
      title = intl.formatMessage(messages.warning);
      body = intl.formatMessage(
        messages.confirmUnassignUnitWithCompletedCertifications,
      );
    }

    setConfirmModalContentState({ title, body });
    openConfirmModal();
  };

  const applicant = visibleApplicants.filter((applicant) =>
    pathOr(false, ['applicantCustomer', 'isProspect'], applicant),
  )[0];

  const convertButtonDisabled =
    !assignedUnit || currentRecord.applicationStatus.name !== 'Approved';

  const routeToAvailableUnits = (options = {}) => {
    if (assignedUnit) {
      getUnitQuote({
        userId: currentUser.id,
        unitId: assignedUnit.unitId,
        applicationId: assignedUnit.applicationId,
        applicantId: applicant.id,
        prospectId,
        refreshActivityTable,
      });
    } else {
      try {
        localStorage.setItem(
          'previous-applicant-id',
          JSON.stringify(currentRecord) + '',
        );

        const baseUrl = `/property/${propertyId}/manage-unit-availability`;
        const searchParams = new URLSearchParams({
          applicantId: applicant.id,
          applicationId: applicationId,
          ...options,
        });

        history.push(`${baseUrl}?${searchParams.toString()}`);
      } catch (e) {
        /* empty */
      }
    }
  };

  const isCommercial = currentRecord.isCommercial === true;
  const hasAssignedUnit = assignedUnit !== null;
  const propertyConfigs = currentRecord?.property?.config ?? {};
  const { assigningUnitsMoveInDates: assigningUnitsMoveInDatesFlag } =
    useFlags();
  const { moveInDateFrom } = getDesiredMoveInDate({
    prospectPreferences,
    isUnitAssigned: Boolean(currentRecord?.au?.unitId),
  });

  // Waitlist V2 shows one move-in date per leasing stage: the editable desired
  // date until a lease exists, the scheduled date after (ENG-32537).
  const hasLease = Boolean(currentRecord.lease?.id);
  const showScheduledMoveInDate = !affordableWaitlistV2 || hasLease;

  const ConfirmModalProps = {
    title: confirmModalContentState.title,
    children: confirmModalContentState.body?.length ? (
      <Box component={'ul'}>
        <Typography component={'li'} paragraph={true}>
          {confirmModalContentState.body}
        </Typography>
      </Box>
    ) : null,
    open: openConfirm,
    actionsProps: [
      { children: 'Cancel', onClick: closeConfirmModal },
      {
        children: 'Confirm',
        onClick: () => {
          unassignUnitCallback();
          closeConfirmModal();
        },
      },
    ],
  };
  return (
    <>
      <Box padding={2}>
        <Stack spacing={2}>
          <IconTitle
            icon={<i key="1" className="et-prospect" />}
            message={messages.snapshot}
          />
          <Stack direction={'row'} spacing={2}>
            <span>
              <div className="custom-checkbox">
                <label>
                  <input
                    type="checkbox"
                    checked={paymentRestrictions.certifiedOnly}
                    disabled={true}
                  />

                  <span className="custom-check-square" />
                  <FormattedMessage {...messages.certifiedOnly} />
                </label>
              </div>
            </span>
            <div className="custom-checkbox">
              <label>
                <input
                  type="checkbox"
                  checked={paymentRestrictions.doNotAccept}
                  disabled={true}
                />
                <span className="custom-check-square" />
                <FormattedMessage {...messages.doNotAccept} />
              </label>
            </div>
          </Stack>
        </Stack>
        <Row className="row">
          <Col xs={12} md={3}>
            <LabelProfileDetails>
              <FormattedMessage {...messages.assignedTo} />
            </LabelProfileDetails>
          </Col>
          <Col xs={12} md={8}>
            <ElementWithPermissions
              scope={['prospect-assign', 'prospect-update']}
            >
              <select
                id="assignedToId"
                name="assignedToId"
                value={currentRecord.assignedToId || ''}
                onChange={(event) =>
                  handleAssignedToChange(event, event.target.value)
                }
              >
                {assignees.map((option) => (
                  <option
                    key={option.value || 'unassigned'}
                    value={option.value}
                    disabled={option.disabled}
                  >
                    {option.text}
                  </option>
                ))}
              </select>
            </ElementWithPermissions>
          </Col>
        </Row>
        <Divider />
        <DetailList>
          {/* An affordable unit puts a set-aside chip on this line too, and with
              four accessibility icons beside it the row runs past the card. Flex
              with wrap lets the assign links drop to their own line instead of
              being squeezed; -webkit-box could not wrap at all. */}
          <li
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              columnGap: 8,
              rowGap: 4,
            }}
          >
            {' '}
            <FormattedMessage {...messages.unitNumber} />
            {` ${unitNumber}`}
            {isAffordable && !isEmpty(unitRentRestriction) && (
              <Typography
                variant="labelMedium"
                color={palette.dark.blue}
                sx={{
                  lineHeight: '20px',
                  marginLeft: '5px',
                  background: palette.lighter.blue,
                  borderRadius: '4px',
                  padding: '0px 4px',
                }}
              >
                {unitRentRestriction}
              </Typography>
            )}
            {/* On the unit's own line, beside the number, matching the current
                resident card. Renders nothing when the server has nothing to
                show, so the line closes up rather than leaving a gap. */}
            {/* Pushed right so it lines up with the assign links, which sit on
                the right of the row below once this one wraps. */}
            <UnitAccessibilityInfo
              organizationId={organizationId}
              propertyId={propertyId}
              householdId={householdId}
              inline
              alignEnd
            />
            <Box marginLeft={'auto'} maxWidth={'fit-content'}>
              <ElementWithPermissions
                scope={['manage-units-update']}
                hidden={true}
              >
                <AssignUnitLink
                  applicantName={applicant?.name}
                  hasAssignedUnit={hasAssignedUnit}
                  routeToAvailableUnits={routeToAvailableUnits}
                  unassignUnit={unassignUnit}
                  areApplicationFeesPaid={areApplicationFeesPaid}
                  propertyConfigs={propertyConfigs}
                  isDisabled={disableUnassignUnitLink}
                  applicationStatusName={currentRecord.applicationStatus?.name}
                  canAssignUnitBeforeFee={canAssignUnitBeforeFee}
                  canAssignUnassignUnit={canAssignUnassignUnit}
                />
              </ElementWithPermissions>
              {applicant && currentUser && !isCommercial
                ? [
                    <span key={1}>{' / '}</span>,
                    <Link key={2} onClick={routeToAvailableUnits}>
                      <FormattedMessage {...messages.provideQuote} />
                    </Link>,
                  ]
                : null}
            </Box>
          </li>
          <FloorPlanDetailRow
            floorPlans={floorPlans}
            unitFloorPlanId={assignedUnit?.unit?.floorPlanId}
            preferredFloorPlanId={prospectPreferences.preferredFloorPlanId}
            propertyId={propertyId}
            action={
              // Once a unit is assigned its floor plan is the qualifying one, so there is
              // nothing left to pick. Ungated, like Provide Quote beside it -- the route
              // enforces application-update server-side.
              !hasAssignedUnit && onSelectFloorPlan ? (
                <Link onClick={onSelectFloorPlan}>
                  <FormattedMessage {...messages.selectFloorPlan} />
                </Link>
              ) : null
            }
          />
          {showScheduledMoveInDate && (
            <li data-testid="scheduledMoveInDate">
              {' '}
              <FormattedMessage {...messages.scheduledMoveInDate} />{' '}
              <FormattedDateOrDashes
                value={scheduledMoveInDate}
                format="MMM DD, YYYY"
              />
            </li>
          )}
          {isCommercial && (
            <li>
              {' '}
              <FormattedMessage {...messages.rentStartDate} />{' '}
              <FormattedDateOrDashes
                value={rentStartDate}
                format="MMM DD, YYYY"
              />
            </li>
          )}
          {!affordableWaitlistV2 && (
            <li data-testid="desiredMoveInDate">
              {' '}
              <FormattedMessage {...messages.desiredMoveInDate} />{' '}
              <FormattedDateOrDashes
                value={
                  assigningUnitsMoveInDatesFlag
                    ? moveInDateFrom
                    : prospectPreferences.moveInDateFrom
                }
                format="MMM DD, YYYY"
              />
            </li>
          )}
          {affordableWaitlistV2 && !hasLease && (
            <DesiredMoveInDateRow
              organizationId={organizationId}
              propertyId={propertyId}
              prospectId={prospectId}
              moveInDateFrom={prospectPreferences.moveInDateFrom}
              onSaved={onDesiredMoveInDateSaved}
            />
          )}
          <li>
            {' '}
            <FormattedMessage {...messages.lastActivity} />{' '}
            {lastActivityType
              ? ` ${lastActivityType.name}, ${lastActivityDateFormatted}`
              : ' ---'}
          </li>
          <li>
            {' '}
            <FormattedMessage {...messages.conversionDate} />{' '}
            <FormattedDateOrDashes
              value={currentRecord.createdAt}
              format="MMM DD, YYYY"
            />
          </li>
        </DetailList>
        {!isCommercial && (
          <ButtonsRow
            handleConvertToResident={handleConvertToResident}
            convertButtonDisabled={convertButtonDisabled}
          />
        )}
        {isCommercial && (
          <ButtonsRow
            handleConvertToCommercialTenant={handleConvertToCommercialTenant}
            convertButtonDisabled={convertButtonDisabled}
          />
        )}
      </Box>
      <ModalConfirm {...ConfirmModalProps} />
    </>
  );
};

export default Snapshot;
